import React, { useState } from 'react';
import { Check } from 'lucide-react';

const PricingSection = ({ onOpenEmailModal }) => {
  const [isAnnual, setIsAnnual] = useState(false);

  const plans = [
    {
      name: 'Free',
      monthlyPrice: '$0',
      annualPrice: '$0',
      monthlyPeriod: ' forever',
      annualPeriod: ' forever',
      description: 'Start your financial journey',
      featured: false,
      features: [
        'Vera — 10 AI messages/day',
        'Basic budgeting tools',
        'Financial Foundation track (8 lessons)',
        'Weekly wins summary',
        'Financial Health Score',
        '1 connected bank account',
      ],
      cta: 'Download Free',
      ctaStyle: 'btn-secondary',
      link: '#download',
      showTrial: false,
    },
    {
      name: 'Pro',
      monthlyPrice: '$9.99',
      annualPrice: '$79.99',
      monthlyPeriod: '/month',
      annualPeriod: '/year',
      annualSavings: '~$6.67/mo, save 33%',
      description: 'Accelerate your progress',
      featured: true,
      badge: 'Most Popular',
      features: [
        'Everything in Free, plus:',
        'Vera — Unlimited messages',
        'Rex — Debt Destroyer coach',
        'Full Academy (30 lessons, 3 tracks)',
        'Full weekly report + Vera Focus',
        'Score history & 90-day trends',
        'Real-time budget alerts',
        'Custom budget categories',
        'Unlimited bank accounts',
        'Partner sharing (2 people)',
      ],
      cta: 'Start Free Trial',
      ctaStyle: 'btn-gold',
      showTrial: true,
    },
    {
      name: 'Premium',
      monthlyPrice: '$19.99',
      annualPrice: '$149.99',
      monthlyPeriod: '/month',
      annualPeriod: '/year',
      annualSavings: '~$12.50/mo, save 37%',
      description: 'Master your wealth',
      featured: false,
      badge: 'Best Value',
      features: [
        'Everything in Pro, plus:',
        'Nova — Investment Guide coach',
        'Investment tracking & insights',
        'Tax optimization tips',
        'White-glove onboarding',
        'Priority support',
      ],
      cta: 'Start Free Trial',
      ctaStyle: 'btn-primary',
      showTrial: true,
    },
  ];

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="bg-[#FAFAF7] section-padding"
    >
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-12">
          <p
            data-testid="pricing-section-label"
            className="font-body text-xs sm:text-sm font-semibold tracking-[0.2em] uppercase text-[#95B8A2] mb-4"
          >
            Simple Pricing
          </p>
          <h2
            data-testid="pricing-section-headline"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-[#2D2D2D] leading-tight mb-8"
          >
            Choose Your Path to
            <br />
            Financial Wellness.
          </h2>

          {/* Monthly/Annual Toggle */}
          <div className="inline-flex items-center gap-3 bg-white rounded-full p-1.5 border border-[#1B4332]/10 shadow-sm">
            <button
              data-testid="pricing-toggle-monthly"
              onClick={() => setIsAnnual(false)}
              className={`px-6 py-2.5 rounded-full font-body font-semibold text-sm transition-all duration-300 ${
                !isAnnual
                  ? 'bg-[#1B4332] text-white shadow-md'
                  : 'text-[#2D2D2D]/70 hover:text-[#1B4332]'
              }`}
            >
              Monthly
            </button>
            <button
              data-testid="pricing-toggle-annual"
              onClick={() => setIsAnnual(true)}
              className={`px-6 py-2.5 rounded-full font-body font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                isAnnual
                  ? 'bg-[#1B4332] text-white shadow-md'
                  : 'text-[#2D2D2D]/70 hover:text-[#1B4332]'
              }`}
            >
              Annual
              <span className={`text-xs px-2 py-0.5 rounded-full ${
                isAnnual ? 'bg-[#D4AF37] text-[#1B4332]' : 'bg-[#D4AF37]/20 text-[#1B4332]'
              }`}>
                Save up to 37%
              </span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto items-stretch">
          {plans.map((plan, index) => (
            <div
              key={plan.name}
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`relative rounded-2xl overflow-hidden animate-fade-in-up ${
                plan.featured
                  ? 'bg-[#0F1F3D] border-2 border-[#D4AF37] scale-105 shadow-2xl shadow-[#D4AF37]/20 z-10'
                  : 'bg-white border border-[#1B4332]/10 shadow-lg'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Badge */}
              {plan.badge && (
                <div className={`absolute top-0 right-0 px-4 py-1.5 text-xs font-body font-semibold ${
                  plan.featured
                    ? 'bg-[#D4AF37] text-[#1B4332]'
                    : 'bg-[#1B4332] text-white'
                } rounded-bl-xl`}>
                  {plan.badge}
                </div>
              )}

              <div className="p-8">
                {/* Plan Name */}
                <h3 className={`font-heading text-xl font-bold mb-2 ${
                  plan.featured ? 'text-[#FAFAF7]' : 'text-[#2D2D2D]'
                }`}>
                  {plan.name}
                </h3>
                
                {/* Description */}
                <p className={`font-body text-sm mb-6 ${
                  plan.featured ? 'text-[#FAFAF7]/70' : 'text-[#2D2D2D]/60'
                }`}>
                  {plan.description}
                </p>

                {/* Price */}
                <div className="mb-2">
                  <span className={`font-mono text-4xl font-bold ${
                    plan.featured ? 'text-[#D4AF37]' : 'text-[#1B4332]'
                  }`}>
                    {isAnnual ? plan.annualPrice : plan.monthlyPrice}
                  </span>
                  <span className={`font-body text-sm ${
                    plan.featured ? 'text-[#FAFAF7]/60' : 'text-[#2D2D2D]/60'
                  }`}>
                    {isAnnual ? plan.annualPeriod : plan.monthlyPeriod}
                  </span>
                </div>

                {/* Annual Savings */}
                {isAnnual && plan.annualSavings && (
                  <p className={`font-body text-sm mb-4 ${
                    plan.featured ? 'text-[#D4AF37]' : 'text-[#2ECC71]'
                  }`}>
                    {plan.annualSavings}
                  </p>
                )}
                {!isAnnual && plan.showTrial && <div className="h-6 mb-4"></div>}

                {/* Trial & Cancel Info */}
                {plan.showTrial && (
                  <div className={`flex flex-wrap gap-2 mb-6 ${
                    plan.featured ? 'text-[#FAFAF7]/70' : 'text-[#2D2D2D]/60'
                  }`}>
                    <span className="font-body text-xs bg-[#1B4332]/10 px-2 py-1 rounded-full">
                      14-day free trial
                    </span>
                    <span className="font-body text-xs bg-[#1B4332]/10 px-2 py-1 rounded-full">
                      Cancel anytime
                    </span>
                  </div>
                )}
                {!plan.showTrial && <div className="h-8 mb-6"></div>}

                {/* Features */}
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, featureIndex) => (
                    <li
                      key={featureIndex}
                      className={`flex items-start gap-3 font-body text-sm ${
                        plan.featured ? 'text-[#FAFAF7]/90' : 'text-[#2D2D2D]/80'
                      }`}
                    >
                      <Check className={`w-4 h-4 flex-shrink-0 mt-0.5 ${
                        plan.featured ? 'text-[#D4AF37]' : 'text-[#1B4332]'
                      }`} />
                      {feature}
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                <a
                  href={plan.link || '#download'}
                  data-testid={`pricing-${plan.name.toLowerCase()}-cta`}
                  className={`block w-full py-4 rounded-full font-body font-semibold text-base text-center transition-all duration-300 ${
                    plan.featured
                      ? 'bg-[#D4AF37] text-[#1B4332] hover:scale-[1.02] shadow-[0_0_20px_rgba(212,175,55,0.3)]'
                      : plan.name === 'Free'
                        ? 'bg-transparent border-2 border-[#1B4332] text-[#1B4332] hover:bg-[#1B4332]/5'
                        : 'bg-[#1B4332] text-white hover:bg-[#1B4332]/90'
                  }`}
                >
                  {plan.cta}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <p className="text-center mt-10 font-body text-sm text-[#2D2D2D]/60">
          ✨ 14-day free trial on all paid plans. Cancel anytime. No questions asked.
        </p>
      </div>
    </section>
  );
};

export default PricingSection;
